<template>
  <div class="d-flex flex-column mt-1 mb-2 justify-content-center">
    <div
      class="input-wrapper"
      :class="{ fill, error, disabled, untouched: highlight && !touched }"
    >
      <div v-if="label && value" class="input-label">
        {{ label }}
      </div>
      <input
        :class="getInputClasses()"
        :maxlength="maxLength"
        :style="inputStyle"
        :type="type"
        :placeholder="placeholder"
        :value="value"
        @input="($event) => handleInput($event.target.value)"
        :onBlur="handleOnBlur"
        :onFocus="handleOnFocus"
        :id="id"
      />
      <div v-if="showErroIcon" class="error-icon-wrapper">
        <i class="fas fa-exclamation" />
      </div>
    </div>
    <div v-if="shouldShowErrorMessage" class="error-msg">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";

export default defineComponent({
  name: "TextInput",
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    maxLength: {
      type: Number,
      default: null,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorIcon: {
      type: Boolean,
      default: true,
    },
    inputStyle: Object,
    centeredText: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    showErrorMessage: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: "",
    },
  },
  emits: ["update:value", "onBlur"],
  setup(props) {
    const showErroIcon = computed(() => {
      if (props.error && props.errorIcon) {
        return true;
      }
      return false;
    });

    const shouldShowErrorMessage = computed(() => {
      if (props.error && props.showErrorMessage) {
        return true;
      }
      return false;
    });

    const touched = ref<boolean>(false);

    return {
      showErroIcon,
      shouldShowErrorMessage,
      touched,
    };
  },
  methods: {
    handleInput(newValue: string) {
      this.$emit("update:value", newValue);
    },
    handleOnBlur() {
      this.$emit("onBlur");
    },
    handleOnFocus() {
      this.touched = true;
    },
    getInputClasses() {
      const classes = {};
      classes["input-element"] = true;
      classes["text-center"] = this.centeredText && true;
      classes["with-label"] = this.value && this.label;
      classes["shrink"] = this.errorMessage !== "" || this.error;
      return classes;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
$pj-color-red-opaque: rgba(222, 14, 14, 0.4);
.input-wrapper {
  height: 50px;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  padding: 5px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.fill {
    width: 100%;
  }

  &.error {
    border: 1px solid $pj-color-red;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  &.untouched {
    border: 1px solid $pj-color-light-green;
  }
}

.input-label {
  position: absolute;
  top: 2px;
  left: 20px;
  font-size: 12px;
  color: $pj-color-cool-gray;
}

.input-element {
  color: $pj-color-black;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  border: none;
  flex-grow: 1;
  height: 100%;
  width: 100%;

  &:focus-visible,
  &:focus,
  &:active {
    outline-width: 0;
  }

  &::placeholder {
    color: $pj-color-cool-gray;
  }
  &.with-label {
    padding-top: 15px;
  }

  &.shrink {
    flex-shrink: 20;
  }
}
.error-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-white;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: $pj-color-red;
  border: 8px solid $pj-color-red-opaque;
  -webkit-background-clip: padding-box; /* for Safari */
  background-clip: padding-box; /* for IE9+, Firefox 4+, Opera, Chrome */
  font-size: 12px;

  i {
    margin: 0;
  }
}

.error-msg {
  color: $pj-color-red;
}
</style>
